import { devLogger } from "./logger";
import {
  getTelegramInitDataRaw,
  getTelegramLaunchParams,
  getTelegramStartParam,
  getTelegramUser,
  isTelegramEnvironment,
} from "./telegramLaunchParams";
import { getCanonicalEnvValue } from "./runtimeEnv";

let analyticsInitialized = false;
let appOpenTracked = false;

export type MiniAppEventName =
  | "app_open"
  | "auth_success"
  | "payment_start"
  | "payment_success"
  | "payment_fail"
  | "ad_request"
  | "ad_show"
  | "ad_complete"
  | "ad_error"
  | "ad_unavailable";

type EventPayload = Record<string, string | number | boolean | null | undefined>;

const sanitizePayload = (payload: EventPayload) =>
  Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined));

const getTelegramAnalyticsToken = () =>
  getCanonicalEnvValue("VITE_TELEGRAM_ANALYTICS_TOKEN");

const initOfficialTelegramAnalytics = async (): Promise<boolean> => {
  const token = getTelegramAnalyticsToken();
  if (!token) {
    return false;
  }

  devLogger.warn(
    "VITE_TELEGRAM_ANALYTICS_TOKEN is configured, but client-side official Telegram analytics SDK is disabled. Falling back to server telemetry.",
  );
  return false;
};

export const trackMiniAppEvent = async (
  eventName: MiniAppEventName,
  payload: EventPayload = {}
) => {
  if (!isTelegramEnvironment()) return;

  try {
    const telegramInitDataRaw = getTelegramInitDataRaw();
    if (!telegramInitDataRaw) {
      devLogger.log(
        `Skipping Telegram custom analytics event "${eventName}" because initData is unavailable.`,
      );
      return;
    }

    const launchParams = getTelegramLaunchParams();
    const telegramUser = getTelegramUser();

    await fetch("/api/analytics/telegram", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Telegram-Init-Data": telegramInitDataRaw,
      },
      keepalive: true,
      body: JSON.stringify({
        eventName,
        payload: sanitizePayload(payload),
        startParam: getTelegramStartParam(),
        platform: launchParams?.platform ?? null,
        telegramUserId: telegramUser?.id ? String(telegramUser.id) : null,
      }),
    });
  } catch (error) {
    devLogger.warn("Telegram custom event tracking failed:", error);
  }
};

export const initTelegramAnalytics = async () => {
  if (analyticsInitialized) return;

  if (!isTelegramEnvironment()) {
    devLogger.log("Not in Telegram environment, skipping analytics init");
    return;
  }

  const officialAnalyticsInitialized = await initOfficialTelegramAnalytics();
  devLogger.log(
    officialAnalyticsInitialized
      ? "Telegram analytics initialized in dual mode (official SDK + server telemetry)."
      : "Telegram analytics initialized in server-telemetry-only mode.",
  );
  analyticsInitialized = true;

  if (!appOpenTracked) {
    appOpenTracked = true;
    void trackMiniAppEvent("app_open", { appVersion: __APP_VERSION__ });
  }
};
